import GppBadIcon from "@mui/icons-material/GppBad";
import GppMaybeIcon from "@mui/icons-material/GppMaybe";
import DeveloperBoardIcon from "@mui/icons-material/DeveloperBoard";
import {
  DiWindows, DiApple, DiLinux, DiAndroid,
} from "react-icons/di";
import {
  SiUbuntu, SiDebian, SiRedhat, SiFedora, SiCentos, SiOpensuse, SiSuse,
  SiAlpinelinux, SiArchlinux, SiGentoo, SiRaspberrypi, SiKalilinux,
  SiRockylinux, SiAlmalinux, SiProxmox,
} from "react-icons/si";
import { resolveClassification, humanizeTaxonomy } from "./assetClassification";

// ── Hostname: choosing ONE of many ───────────────────────────────────────────
// `host_names` is a <source>_name → name map; an asset can easily carry four
// disagreeing names. The priority below is Orion's, and its rationale is that the
// more self-declared a name is, the more it is worth: what the host asks to be
// called beats what it announces to the segment, which beats what *other* systems
// use to reach it.
//
// ⚠ DIVERGENCE, ON PURPOSE: the firmware's own `_HOSTNAME_SOURCE_PRIORITY`
// (AssetModel.py) puts `dns_name` FIRST — the opposite end of this list. That is a
// real disagreement between the two components, not a bug on this side: a DNS name
// is a label other systems hang on the host, not something the host says about
// itself. Do not "align" this list with the firmware's without settling which one
// is right first.
export const HOSTNAME_SOURCE_PRIORITY = [
  // 1) the name the host asks for itself
  "dhcp_name",
  // 2) self-announcements to the segment
  "mdns_name", "llmnr_name", "lldp_name", "cdp_name", "dicom_ae_name", "cldap_name",
  // `profinet_name` is not placed by the spec's tier list; it is a station name the
  // device declares about itself, so it sits with the other self-announcements.
  "profinet_name",
  // 3) names negotiated during authentication
  "netbios_name", "ntlm_name",
  // 4) names others use to reach it — weakest evidence of identity
  "dns_name", "tls_sni_name", "tls_cn_name",
];

const HOSTNAME_SOURCE_LABELS = {
  dhcp_name:     "DHCP",
  mdns_name:     "mDNS",
  llmnr_name:    "LLMNR",
  lldp_name:     "LLDP",
  cdp_name:      "CDP",
  dicom_ae_name: "DICOM AE",
  cldap_name:    "CLDAP",
  profinet_name: "PROFINET",
  netbios_name:  "NetBIOS",
  ntlm_name:     "NTLM",
  dns_name:      "DNS",
  tls_sni_name:  "TLS SNI",
  tls_cn_name:   "TLS CN",
};

export const hostnameSourceLabel = (key) =>
  HOSTNAME_SOURCE_LABELS[key] ?? String(key ?? "").replace(/_name$/, "").toUpperCase();

// The winning name plus where it came from. A source we don't know yet still beats
// showing nothing, so unranked keys are used as a last resort rather than dropped.
export const pickHostname = (hostNames) => {
  const map = hostNames || {};
  for (const key of HOSTNAME_SOURCE_PRIORITY) {
    if (map[key]) return { name: String(map[key]), source: key, label: hostnameSourceLabel(key) };
  }
  const fallback = Object.entries(map).find(([, v]) => v);
  return fallback
    ? { name: String(fallback[1]), source: fallback[0], label: hostnameSourceLabel(fallback[0]) }
    : { name: "", source: "", label: "" };
};

// Every other name the asset carries — the answer to "why am I seeing this one and
// not that one", which is the only reason a discordant set of names is worth showing.
export const hostnameAlternatives = (hostNames, chosenSource) =>
  Object.entries(hostNames || {})
    .filter(([key, value]) => value && key !== chosenSource)
    .map(([key, value]) => ({ source: key, label: hostnameSourceLabel(key), name: String(value) }));

// Identity to display, in the order the dashboard is required to fall back:
// hostname → IP → MAC. `asset_key` is an internal key and never appears here.
export const assetIdentity = (row) =>
  row?.machine_name || row?.hostname || row?.ip || row?.mac || "";

// ── Shaping raw documents into grid/detail rows ───────────────────────────────
// Single source of truth for field names and defaults, shared by the Inventory
// grid, the Overview tab, the drawer and the full Asset Detail page — so those
// views can never drift apart on how they read the document.
//
// Cohesive groups (os / snmp / dhcp / device / classification / fingerprint /
// zone) are passed through as `null` when the document omits them, so every
// renderer can gate a whole section on one truthy check.
//
// NOT mapped, deliberately: `asset_key` (an internal primary key), `source_engine`
// (an implementation detail of the pipeline, not a fact about the asset),
// `machine_id` / `tenant_id` / `sensor_id` (infrastructure), and `promoted_from` /
// `mac_observations` (pipeline bookkeeping — which provisional key an asset was
// tracked under and which worker saw which MAC say nothing about the asset itself).
// Leaving them off the row is what guarantees no view can surface them by accident.
// Status is derived from last_seen alone, not trusted from the document's own
// `status` field: online if seen within the window, offline past it — a stale
// document (probe down, ingestion lag, ...) should not keep showing a host as
// online forever, nor should a fresh one sit as "unknown" just because the
// pipeline never wrote a `status` field. `item.status` is only a fallback for
// the (should-not-happen) case where there's no `last_seen` to derive from.
const OFFLINE_AFTER_MS = 24 * 60 * 60 * 1000;

const deriveStatus = (item, lastSeen) => {
  if (lastSeen == null) return item.status ?? null;
  const isStale = Date.now() - new Date(lastSeen).getTime() > OFFLINE_AFTER_MS;
  return isStale ? "offline" : "online";
};

export const mapAssetRows = (data) => {
  return (data || []).map((item, index) => {
    const host_names = item.host_names ?? {};
    const chosenHost = pickHostname(host_names);
    const classification = item.classification ?? null;
    const risk = deriveRisk(item);
    const last_seen = item.last_seen?.$date ?? item.last_seen ?? null;

    return {
      // _id is a plain string from Mongoose JSON; $oid format only in Extended JSON exports
      id: item._id?.$oid ?? item._id ?? `asset-${index}`,

      // ── envelope ──────────────────────────────────────────────────────
      // status is derived from last_seen (offline past 24h), not trusted as-is
      // from the document — see deriveStatus() above.
      status: deriveStatus(item, last_seen),
      first_seen: item.first_seen?.$date ?? item.first_seen ?? null,
      last_seen,
      snmp_last_scan: item.snmp_last_scan?.$date ?? item.snmp_last_scan ?? null,

      // ── core / network (flat) ────────────────────────────────────────
      ip: item.ip ?? "",
      ip_v6: item.ip_v6 ?? "",
      mac: item.mac ?? "",
      alt_macs: item.alt_macs ?? [],
      vendor: item.vendor ?? "",
      // "oui" = inferred from the MAC prefix; "device-identity" = declared by the
      // device itself, and therefore more trustworthy.
      vendor_source: item.vendor_source ?? "",
      vlan: item.vlan ?? null,
      inner_vlan: item.inner_vlan ?? null,
      // transitions: [{ old_ip, new_ip, changed_at, source }] — NOT a list of
      // addresses held over time
      ip_history: item.ip_history ?? [],
      status_history: item.status_history ?? [],

      // ── hostname ─────────────────────────────────────────────────────
      // The document has no flat `hostname` any more; these three are derived here
      // from `host_names` so grid columns and filters have one field to read.
      host_names,
      hostname: chosenHost.name,
      hostname_source: chosenHost.source,
      hostname_source_label: chosenHost.label,

      // ── cohesive groups — omitted (null) if the document didn't carry them ──
      os: normalizeOs(item.os),
      snmp: item.snmp ?? null,
      dhcp: item.dhcp ?? null,
      device: item.device ?? null,
      // Open-ended, keyed by protocol (`modbus`, `s7`, `bacnet`, `dicom`, `onvif`,
      // `snmp_entity_mib`, an IPP printer, ...) — see ProtocolIdentitySection.jsx.
      // Passed through whole rather than picked apart field-by-field here, because
      // the probe adds new protocol keys with no whitelist and this mapper would
      // otherwise need a change every time it does.
      protocols: item.protocols ?? null,
      classification,
      fingerprint: item.fingerprint ?? null,
      // The probe's own zone/Purdue group — distinct from the dashboard's manual
      // Zones feature below (`zone_id`), which is a different concept entirely.
      zone: item.zone ?? null,

      // Flattened for filters/charts. Empty means the document never reported one
      // (an old document); "unclassified" is a real taxonomy answer and not the same
      // thing.
      asset_type: classification?.asset_type?.value ?? "",

      // ── risk ─────────────────────────────────────────────────────────
      // is_gateway is always emitted, including false — `?? null` here only ever
      // catches a document predating the field.
      // risk_score/risk_factors are derived by deriveRisk() above: it prefers the
      // rules-engine's `risk` field (asset-classification-task TASK-AI-014) and
      // falls back to the legacy flat fields for documents not yet reprocessed.
      risk_score: risk.score,
      risk_factors: risk.factors,
      // Consequence-if-compromised vs. how-exposed-today sub-scores (TASK-AI-032) —
      // null for documents predating that split (or the rules engine entirely).
      risk_impact: risk.impact,
      risk_likelihood: risk.likelihood,
      // Full rules-engine structure (value/confidence per factor), for any future
      // renderer that wants more than the legacy "<reason>:+<weight>" strings —
      // null for documents that predate the rules engine.
      risk_detail: item.risk ?? null,
      // Recomputed on a schedule from live telemetry (network/security events), not
      // just at classification time — same weighted factors as risk_score/risk_factors
      // plus event-driven ones (`security_events_<tier>(matched/total)`,
      // `ot_security_events_<tier>(matched/total)`) that only exist here. This is the
      // score the Security tab now leads with; risk_score stays as the static baseline.
      // No rules-engine nested shape for this one (yet) — flat fields only, so no
      // fallback needed; `?? null`/`?? []` just cover documents that predate the field.
      dynamic_risk_score: typeof item.dynamic_risk_score === "number" ? item.dynamic_risk_score : null,
      dynamic_risk_factors: item.dynamic_risk_factors ?? [],
      is_gateway: typeof item.is_gateway === "boolean" ? item.is_gateway : null,

      // ── lists ─────────────────────────────────────────────────────────
      // `services` now holds ONLY exposed server-side ports — client entries are no
      // longer produced, so nothing downstream needs to filter on `type`.
      services: item.services ?? [],
      new_ports: item.new_ports ?? [],
      software: item.software ?? [],
      vulnerabilities: item.vulnerabilities ?? [],
      // OT/antlia operation catalog: [{ protocol, role, op_class, operation, is_write,
      // first_seen, last_seen }] — which operations this asset performed/received,
      // as client or server. See pipeline's OtOperationsAggregator.
      operations: item.operations ?? [],
      antlia: item.antlia ?? null,

      allTags: [...new Set((item.services || []).flatMap((s) => s.tags || []))],

      // ── dashboard-managed fields — not part of the document's schema,
      // written back onto the same document by the Zones/notes/custom-name/
      // favourite features ──────────────────────────────────────────────
      zone_id: item.zone_id ?? null,
      notes: item.notes ?? "",
      machine_name: item.machine_name ?? "",
      pref: item.pref ?? 0,
    };
  });
};

// ── OS ───────────────────────────────────────────────────────────────────────
// The four levels of `os` are independent: none implies the others, each can be
// absent, and each carries its own confidence and provenance.
//
// The rename that trips people up: Orion's internal `os_name` is emitted as
// `os.version` (the commercial release name, "Windows 11 Pro"), and its internal
// `os_version` is emitted as `os.build` (the build number, "10.0.26100"). Reading
// the old flat shape and mapping it positionally swaps them.
//
// `os.distribution` (asset-classification-task TASK_SPEC_ESTRAZIONE_NORMALIZZAZIONE_RULES.md
// §1, `resolution.os.distribution`) carries the Linux distro name (e.g. "Ubuntu",
// "Debian", "Rocky Linux") separately from `os.version`, which for Linux assets
// holds only the bare release number (e.g. "22.04") once a rule resolves both —
// so a Linux release reads as "Ubuntu" + "22.04", not "22.04" alone.
//
// Orion has emitted this group two different ways: flat (`family` + a sibling
// `family_confidence`/`family_source`) and nested (`family: {value, confidence,
// source}`, mirroring the `classification` group's shape). Every renderer below
// reads the flat shape, so this collapses whichever one arrived down to it once,
// here, instead of teaching every render site both shapes.
const osLevel = (os, key) => {
  const node = os?.[key];
  const isNested = node && typeof node === "object" && !Array.isArray(node);
  return {
    value: isNested ? (node.value ?? "") : (node ?? ""),
    confidence: isNested && typeof node.confidence === "number" ? node.confidence
      : typeof os?.[`${key}_confidence`] === "number" ? os[`${key}_confidence`] : null,
    source: (isNested && node.source) || os?.[`${key}_source`] || null,
  };
};

export const normalizeOs = (os) => {
  if (!os) return null;
  const family = osLevel(os, "family");
  const distribution = osLevel(os, "distribution");
  const version = osLevel(os, "version");
  const build = osLevel(os, "build");
  return {
    family: family.value, family_confidence: family.confidence, family_source: family.source,
    distribution: distribution.value, distribution_confidence: distribution.confidence, distribution_source: distribution.source,
    version: version.value, version_confidence: version.confidence, version_source: version.source,
    build: build.value, build_confidence: build.confidence, build_source: build.source,
  };
};

export const OS_LEVELS = [
  { key: "family",       label: "Family",       hint: "Windows, Linux, MacOS, Android, iOS, Embedded" },
  { key: "distribution", label: "Distribution", hint: "Linux distribution, e.g. Ubuntu, Debian, Rocky Linux" },
  { key: "version",      label: "Release",      hint: "Commercial release name, e.g. Windows 11 Pro, or the bare release number for Linux" },
  { key: "build",        label: "Build",        hint: "Build number, e.g. 10.0.26100" },
];

// The fullest Linux release label available: "Ubuntu 22.04" when both distro and
// release number are known, just the distro name or bare version otherwise.
export const osReleaseLabel = (os) => [os?.distribution, os?.version].filter(Boolean).join(" ");

// `os.family` values are capitalised, with two irregular spellings — `iOS` (lower
// i) and `MacOS`. Any comparison has to normalise; assuming plain lowercase
// silently misses both.
const OS_FAMILY_ICONS = {
  windows:  DiWindows,
  linux:    DiLinux,
  "linux/unix": DiLinux,
  macos:    DiApple,
  ios:      DiApple,
  android:  DiAndroid,
  embedded: DeveloperBoardIcon,
};

// A resolved `os.distribution` (e.g. "Ubuntu") carries no literal "linux" token
// at all, so distro detection has to run before — not after — the generic
// "linux" check below. Order matters: more specific tokens (rhel, opensuse) must
// be tested before substrings they contain (redhat's "hat" vs. suse's "suse").
const LINUX_DISTRO_ICONS = [
  { test: /kali/, icon: SiKalilinux },
  { test: /raspberry ?pi|raspbian/, icon: SiRaspberrypi },
  { test: /ubuntu/, icon: SiUbuntu },
  { test: /debian/, icon: SiDebian },
  { test: /rocky/, icon: SiRockylinux },
  { test: /alma\s?linux/, icon: SiAlmalinux },
  { test: /red\s?hat|rhel/, icon: SiRedhat },
  { test: /centos/, icon: SiCentos },
  { test: /fedora/, icon: SiFedora },
  { test: /opensuse/, icon: SiOpensuse },
  { test: /\bsuse\b/, icon: SiSuse },
  { test: /alpine/, icon: SiAlpinelinux },
  { test: /\barch\s?linux\b/, icon: SiArchlinux },
  { test: /gentoo/, icon: SiGentoo },
  { test: /proxmox/, icon: SiProxmox },
];

export const osIcon = (os) => {
  const family = String(os?.family ?? "").trim().toLowerCase();
  const osText = [os?.family, os?.distribution, os?.version].filter(Boolean).join(" ").toLowerCase();
  if (!osText) return null;
  if (osText.includes("windows")) return DiWindows;
  const distro = LINUX_DISTRO_ICONS.find(({ test }) => test.test(osText));
  if (distro) return distro.icon;
  if (osText.includes("linux")) return DiLinux;
  return OS_FAMILY_ICONS[family] ?? null;
};

// The most specific OS label available, for one-line summaries (grid cell, tooltip).
export const osSummary = (os) => osReleaseLabel(os) || os?.family || "";

// ── Confidence ───────────────────────────────────────────────────────────────
// Confidences are 0–100 and derive from the *kind* of evidence: ~90 means the
// system declared its own OS in a field of its own, ~40 a substring match in a
// banner, ~25 pure inference from the TCP stack. The gap between 90 and 25 is the
// gap between a fact and a guess, so it is always rendered — never rounded away.
export const confidenceStyle = (confidence) => {
  if (typeof confidence !== "number") return null;
  if (confidence >= 75) return { color: "#4cceac", label: "strong evidence" };
  if (confidence >= 50) return { color: "#ffb422", label: "partial evidence" };
  return { color: "#ff7744", label: "weak evidence — inferred" };
};

// ── Risk (rules-engine `risk` field, TASK-AI-014 + TASK-AI-032) ───────────────
// Orion used to emit `risk_score` (flat 0-100) + `risk_factors` (array of
// "<reason>:+<weight>" strings) directly. That computation moved to the rules
// engine (asset-classification-task repo, third pass, `pass: 3` rules), which
// instead writes a nested `risk` field on the document:
//
//   risk.value.value            -> the 0-100 score
//   risk.impact.value           -> consequence IF this asset is compromised
//                                  (taxonomy role, Purdue proximity, "ics-critical" tag)
//   risk.likelihood.value       -> how exposed/attackable it looks TODAY
//                                  (unauthenticated/cleartext/remote-access tags, EOL OS,
//                                  gateway role, ...)
//   risk.factors.<name>.value   -> the weight of one contributing factor (either dimension)
//   risk.factors.<name>.source  -> the reason string, reusing the SAME
//                                  "<prefix>_<detail>" conventions
//                                  riskFactorLabel() below already parses
//                                  (e.g. "tag_ics-critical", "os_eol_windows_7",
//                                  "purdue_1", "gateway", or a bare taxonomy
//                                  role like "plc")
//
// `value` is Impact*(0.6 + 0.4*Likelihood/100) since TASK-AI-032, not a flat sum of every
// factor anymore: summing meant a low-criticality device with a handful of exposure tags
// could outscore an unexposed safety controller, since a sum only counts how many signals
// fired, not what they mean. impact/likelihood are exposed alongside the score for a future
// breakdown view; nothing today reads them besides this pass-through.
//
// deriveRisk() converts the new shape back into the OLD score/factors pair so
// every existing renderer (AssetRiskChip, AssetDetailContent's RiskScoreCard,
// AssetsKpiBar, the custom-filter builder, ...) keeps working unchanged. A
// document not yet reprocessed by the rules engine has no `risk` field at
// all, so this always falls back to the legacy flat fields — nothing
// regresses for older data.
const deriveRisk = (item) => {
  const newScore = item.risk?.value?.value;
  const score = typeof newScore === "number"
    ? newScore
    : (typeof item.risk_score === "number" ? item.risk_score : null);

  const newFactors = item.risk?.factors;
  const factors = (newFactors && typeof newFactors === "object")
    ? Object.values(newFactors)
      .filter((f) => f && typeof f.value === "number")
      .map((f) => `${f.source || "factor"}:+${f.value}`)
    : (item.risk_factors ?? []);

  const impact = item.risk?.impact?.value;
  const likelihood = item.risk?.likelihood?.value;

  return {
    score,
    factors,
    impact: typeof impact === "number" ? impact : null,
    likelihood: typeof likelihood === "number" ? likelihood : null,
  };
};

// ── Risk score ───────────────────────────────────────────────────────────────
const RISK_BANDS = [
  { min: 80, color: "#ff4444", bg: "#ff222233", label: "Critical" },
  { min: 60, color: "#ff7744", bg: "#ff552233", label: "High" },
  { min: 40, color: "#ffb422", bg: "#ffb42233", label: "Elevated" },
  { min: 20, color: "#7b8bff", bg: "#4254fb33", label: "Moderate" },
  { min: 0,  color: "#4cceac", bg: "#4cceac22", label: "Low" },
];

export const riskScoreStyle = (score) => {
  if (typeof score !== "number") return { color: "#9a9a9a", bg: "#88888822", label: "—" };
  return RISK_BANDS.find((b) => score >= b.min) ?? RISK_BANDS[RISK_BANDS.length - 1];
};

// `security_events_<tier>(matched/total)` / `ot_security_events_<tier>(matched/total)` —
// dynamic_risk_factors-only reasons: how many of the asset's recent security events
// (all, or specifically OT-protocol ones) were flagged at that severity tier.
const parseEventRatio = (rest) => {
  const m = /^(\w+)\((\d+)\/(\d+)\)$/.exec(rest);
  if (!m) return null;
  const [, tier, matched, total] = m;
  return { tier, matched, total };
};

// Prefixes whose suffix carries the actual detail, so the label is built rather
// than looked up (`purdue_1`, `tag_ics-critical`, `os_eol_windows_7`, `vuln_critical`).
const RISK_FACTOR_PATTERNS = [
  { prefix: "purdue_",    describe: (rest) => `Purdue level ${rest} — proximity to the physical process` },
  { prefix: "target_sl_", describe: (rest) => `Target security level ${rest}` },
  { prefix: "tag_",       describe: (rest) => `Service tagged "${rest}"` },
  { prefix: "os_eol_",    describe: (rest) => `Operating system out of support: ${humanizeTaxonomy(rest)}` },
  { prefix: "vuln_",      describe: (rest) => `${humanizeTaxonomy(rest)} vulnerabilities` },
  { prefix: "zone_",      describe: (rest) => `Zone manually assessed as ${humanizeTaxonomy(rest)} risk` },
  {
    prefix: "ot_security_events_",
    describe: (rest) => {
      const r = parseEventRatio(rest);
      return r ? `OT security events: ${r.matched} of ${r.total} flagged ${r.tier}` : `OT security events (${rest})`;
    },
  },
  {
    prefix: "security_events_",
    describe: (rest) => {
      const r = parseEventRatio(rest);
      return r ? `Security events: ${r.matched} of ${r.total} flagged ${r.tier}` : `Security events (${rest})`;
    },
  },
];

const RISK_FACTOR_LABELS = {
  gateway:             "Gateway — sees traffic that isn't its own",
  ot_client_reach:     "Speaks control protocols as a client",
  network_connections: "Elevated recent network connection volume",
};

export const riskFactorLabel = (reason) => {
  const key = String(reason ?? "");
  if (RISK_FACTOR_LABELS[key]) return RISK_FACTOR_LABELS[key];
  const pattern = RISK_FACTOR_PATTERNS.find((p) => key.startsWith(p.prefix));
  if (pattern) return pattern.describe(key.slice(pattern.prefix.length));
  // Anything left is the role from the taxonomy (`plc`, `ct_scanner`, ...)
  return humanizeTaxonomy(key);
};

// `risk_factors`/`dynamic_risk_factors` entries are "<reason>:+<weight>" strings
// that sum to their respective score. A bare number is not actionable and the
// weights are too many for an operator to reconstruct, so this is what turns
// the score into an explanation.
export const parseRiskFactors = (factors) =>
  (factors || [])
    .map((raw) => {
      const s = String(raw);
      const idx = s.lastIndexOf(":");
      if (idx < 0) return { reason: s, weight: null, label: riskFactorLabel(s), raw: s };
      const reason = s.slice(0, idx);
      const weight = Number(s.slice(idx + 1).replace("+", ""));
      return {
        reason,
        weight: Number.isFinite(weight) ? weight : null,
        label: riskFactorLabel(reason),
        raw: s,
      };
    })
    .sort((a, b) => (b.weight ?? 0) - (a.weight ?? 0));

// dynamic_risk_factors is a superset of risk_factors (same declarative signals —
// zone risk, tags, EOL OS, ... — plus live event-driven ones risk_score never
// sees, e.g. `security_events_<tier>(matched/total)`). Marks each dynamic factor
// as `shared` (also drives the static score) so a renderer can tell "this pushed
// both scores up" from "this is why the two disagree" without duplicating the
// static list.
export const mergeRiskFactors = (staticFactors, dynamicFactors) => {
  const staticReasons = new Set(parseRiskFactors(staticFactors).map((f) => f.reason));
  return parseRiskFactors(dynamicFactors).map((f) => ({ ...f, shared: staticReasons.has(f.reason) }));
};

// ── ip_history ───────────────────────────────────────────────────────────────
// Signals that actually observed the change, versus `snapshot`, which is inferred
// by diffing against the previous state and is therefore weaker evidence. A
// `source` naming the engine (orion/scapy) means the document predates the change
// — the engine is not a signal.
const IP_CHANGE_SIGNALS = {
  arp:      { label: "ARP",      strong: true,  note: "observed on the wire" },
  dhcp:     { label: "DHCP",     strong: true,  note: "observed in a DHCP exchange" },
  conn:     { label: "Traffic",  strong: true,  note: "observed in a connection" },
  eui64:    { label: "EUI-64",   strong: true,  note: "derived from the IPv6 interface identifier" },
  snapshot: { label: "Snapshot", strong: false, note: "inferred by comparison with the previous state — weaker than an observed signal" },
};

const ENGINE_NAMES = new Set(["orion", "scapy"]);

export const ipChangeSignal = (source) => {
  const key = String(source ?? "").toLowerCase();
  if (IP_CHANGE_SIGNALS[key]) return IP_CHANGE_SIGNALS[key];
  if (ENGINE_NAMES.has(key)) {
    return { label: key, strong: false, note: "this is the engine name, not a detection signal — the entry comes from an older document", stale: true };
  }
  return key ? { label: key, strong: false, note: "unrecognised signal" } : null;
};

// { old_ip, new_ip, changed_at, source } normalised, newest first.
export const parseIpTransitions = (ipHistory) =>
  (ipHistory || [])
    .map((h) => {
      if (typeof h === "string") return { old_ip: null, new_ip: h, changed_at: null, source: null, signal: null };
      return {
        old_ip: h.old_ip ?? null,
        new_ip: h.new_ip ?? null,
        changed_at: h.changed_at?.$date ?? h.changed_at ?? null,
        source: h.source ?? null,
        signal: ipChangeSignal(h.source),
      };
    })
    .sort((a, b) => new Date(b.changed_at ?? 0) - new Date(a.changed_at ?? 0));

// ── Service tags ─────────────────────────────────────────────────────────────
const FALLBACK_COLORS = {
  "high-risk":       { bg: "#ff222233", color: "#ff4444" },
  "ics-critical":    { bg: "#ff222233", color: "#ff4444" },
  "vulnerable":      { bg: "#ff552233", color: "#ff7744" },
  "unauthenticated": { bg: "#ff552233", color: "#ff7744" },
  "cleartext":       { bg: "#ffb42233", color: "#ffb422" },
  "remote-access":   { bg: "#ffb42233", color: "#ffb422" },
  "management":      { bg: "#4254fb33", color: "#7b8bff" },
  "industrial":      { bg: "#4254fb33", color: "#7b8bff" },
  "healthcare":      { bg: "#ff6b9d22", color: "#ff6b9d" },
  "network":         { bg: "#4254fb33", color: "#4254fb" },
  "encrypted":       { bg: "#4cceac22", color: "#4cceac" },
};
const DEFAULT_COLOR = { bg: "#88888822", color: "#aaaaaa" };

// Factory: returns a resolver bound to a dynamic map from system_configurations.
// Call with no args for the legacy static resolver.
export const makeTagColor = (dynamicMap = {}) => (tag) => {
  const t = tag?.toLowerCase();
  return dynamicMap[t] ?? FALLBACK_COLORS[t] ?? DEFAULT_COLOR;
};

// Backward-compatible static resolver (used where no dynamic map is available)
export const tagColor = makeTagColor();

// ── Vulnerability severity helpers ───────────────────────────────────────────
// The canonical vulnerabilities[] shape is { cve_id, severity, description,
// matched_on } — a plain severity label straight from the pipeline, not a CVSS
// score. bestCvss/bestCvssVersion/severityTier(score) below stay only as a
// defensive fallback for any legacy-shaped vuln that might still carry
// cvss_v2/cvss_v3 instead.

export const bestCvss = (vuln) => {
  const v3 = vuln?.cvss_v3;
  const v2 = vuln?.cvss_v2;
  return typeof v3 === "number" ? v3 : (typeof v2 === "number" ? v2 : null);
};

export const bestCvssVersion = (vuln) => {
  if (typeof vuln?.cvss_v3 === "number") return "v3";
  if (typeof vuln?.cvss_v2 === "number") return "v2";
  return null;
};

// CVSS score → severity tier — legacy fallback path only
export const severityTier = (score) => {
  if (score == null) return "none";
  if (score >= 9)     return "critical";
  if (score >= 7)      return "high";
  if (score >= 4)      return "medium";
  if (score > 0)       return "low";
  return "none";
};

const KNOWN_TIERS = ["critical", "high", "medium", "low"];

// Shared worst-first tier order, "none" (no severity/CVSS data at all) last —
// used anywhere severity gets bucketed into sections (VulnerabilityList.jsx's
// per-tier groups, the Home widget's per-asset severity chips, ...).
export const SEVERITY_TIER_ORDER = [...KNOWN_TIERS, "none"];

// A vuln's tier — prefers the pipeline's own `severity` label, falls back to a
// CVSS score for any legacy-shaped entry that has one instead.
export const vulnSeverityTier = (vuln) => {
  const raw = String(vuln?.severity ?? "").toLowerCase().trim();
  if (KNOWN_TIERS.includes(raw)) return raw;
  return severityTier(bestCvss(vuln));
};

const SEVERITY_STYLES = {
  critical: { bg: "#ff222233", color: "#ff4444", label: "Critical" },
  high:     { bg: "#ff552233", color: "#ff7744", label: "High" },
  medium:   { bg: "#ffb42233", color: "#ffb422", label: "Medium" },
  low:      { bg: "#4254fb33", color: "#7b8bff", label: "Low" },
  // Not a real tier (vulnSeverityTier() never returns this) — only used by the
  // Zones feature's manually-assessed risk level, one step below "low".
  verylow:  { bg: "#4cceac33", color: "#4cceac", label: "Very Low" },
  none:     { bg: "#88888822", color: "#aaaaaa", label: "Unknown" },
};

// CVSS score → color/label, same alpha-suffixed palette style as FALLBACK_COLORS above
export const severityColor = (score) => SEVERITY_STYLES[severityTier(score)];

// tier ('critical'|'high'|'medium'|'low'|'none') → color/label, for callers that
// already bucket by tier (e.g. counting vulnerabilities per severity for a KPI tile)
export const severityStyleForTier = (tier) => SEVERITY_STYLES[tier] ?? SEVERITY_STYLES.none;

const SEVERITY_ICONS = {
  critical: GppBadIcon,
  high:     GppBadIcon,
  medium:   GppMaybeIcon,
  low:      GppMaybeIcon,
  verylow:  null,
  none:     null,
};

// CVSS score → icon component
export const severityIcon = (score) => SEVERITY_ICONS[severityTier(score)];

// tier → icon component, for callers that already bucket by tier
export const severityIconForTier = (tier) => SEVERITY_ICONS[tier] ?? null;

const SEVERITY_RANK = { critical: 4, high: 3, medium: 2, low: 1, none: 0 };

// A vuln's rank for sorting "worst first" — derived from vulnSeverityTier
export const vulnSeverityRank = (vuln) => SEVERITY_RANK[vulnSeverityTier(vuln)] ?? 0;

// Highest-severity entry in a vulnerabilities array (for badges/sorting)
export const worstVulnerability = (vulns) => {
  const list = vulns || [];
  if (list.length === 0) return null;
  return list.reduce((worst, v) => (!worst || vulnSeverityRank(v) > vulnSeverityRank(worst) ? v : worst), null);
};

// Every vulnerability on the asset — flat under the canonical schema (services
// no longer carry their own embedded vulnerabilities). `matched_on` (what the
// match was made against — a service, a software package, ...) doubles as both
// the display origin and the stable key CVE acknowledgments are matched on, so
// an ack survives a rescan instead of silently detaching.
export const allVulnerabilitiesForAsset = (row) =>
  (row?.vulnerabilities || []).map((v) => ({ ...v, _origin: v.matched_on ?? null, _originKey: v.matched_on ?? null }));

// Shared key for matching a vulnerability against its CVE acknowledgment record —
// used identically by securityScore.js, VulnerabilityList's ack UI, and the ack API calls.
export const vulnAckKey = (assetId, cveId, originKey) => `${assetId}|${cveId}|${originKey ?? ""}`;

// Heuristic network-exposure score for an asset — external reachability first
// (dominant signal, +1000), then how many services are exposed (+10 each),
// then raw connection volume (from `exposureByIp`, the bulk map returned by
// API.getAssetExposure() and keyed by IP). Used only as a tie-breaker between
// CVEs/assets of equal severity wherever CVEs are listed — it never changes
// which severity sorts first, only the order among ties. `exposureByIp` may
// be `{}`/undefined (still loading, or no recent connection data) — falls
// back to exposed-service count alone, which is always available on the row.
export const exposureScoreFor = (row, exposureByIp) => {
  const exp = exposureByIp?.[row?.ip];
  const servicesCount = row?.services?.length || 0;
  if (!exp) return servicesCount * 10;
  return (exp.hasExternalConnections ? 1000 : 0) + servicesCount * 10 + (exp.connCount || 0);
};

// ── Filtering ────────────────────────────────────────────────────────────────
const numericCompare = (actual, operator, value) => {
  const a = Number(actual);
  const b = Number(value);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return false;
  if (operator === "gte") return a >= b;
  if (operator === "lte") return a <= b;
  if (operator === "notEquals") return a !== b;
  return a === b;
};

const matchFilter = (row, { field, operator, value }) => {
  const strVal = String(value ?? "").toLowerCase();
  const test = (str) => {
    const s = String(str ?? "").toLowerCase();
    if (operator === "contains")    return s.includes(strVal);
    if (operator === "notContains") return !s.includes(strVal);
    if (operator === "equals")      return s === strVal;
    if (operator === "notEquals")   return s !== strVal;
    return true;
  };
  if (field === "tag")
    return (row.allTags || []).some(test);
  if (field === "ip_history")
    // Transitions, not addresses: an entry has an `old_ip` and a `new_ip`, and a
    // search for an address should hit whichever side it appears on.
    return parseIpTransitions(row.ip_history).some((t) => test(t.old_ip) || test(t.new_ip));
  if (field === "service_name")
    return (row.services || []).some((s) => test(s.name));
  if (field === "service_port")
    return (row.services || []).some((s) => numericCompare(s.port, operator, value));
  if (field === "hostname")
    return test(row.hostname || row.machine_name);
  if (field === "os_family")
    return test([row.os?.family, row.os?.version].filter(Boolean).join(" "));
  if (field === "os_version")
    return test(row.os?.version);
  if (field === "os_build")
    return test(row.os?.build);
  if (field === "firmware")
    return test(row.device?.firmware);
  if (field === "device_model")
    return test(row.device?.model);
  if (field === "classification") {
    // Matches any level, so "plc", "control" and "ot" all find the same asset.
    const c = resolveClassification(row.classification);
    return test(c.assetType) || test(c.subcategory) || test(c.type) || test(c.path);
  }
  if (field === "risk_score") {
    // Same fallback as the grid's RISK column: prefer the live dynamic_risk_score so
    // a "Critical" filter always agrees with what the column is actually showing.
    const effectiveScore = typeof row.dynamic_risk_score === "number"
      ? row.dynamic_risk_score
      : (typeof row.risk_score === "number" ? row.risk_score : null);
    const band = String(value ?? "").toLowerCase();
    if (["critical", "high", "elevated", "moderate", "low"].includes(band)) {
      return typeof effectiveScore === "number" && riskScoreStyle(effectiveScore).label.toLowerCase() === band;
    }
    return numericCompare(effectiveScore, operator, value);
  }
  if (field === "is_gateway")
    return String(row.is_gateway === true ? "1" : "0") === String(value);
  if (field === "purdue_level")
    return test(row.zone?.purdue_level ?? row.zone?.purdue_level_auto?.value);
  if (field === "probe_zone")
    return test(row.zone?.name);
  if (field === "risk_severity")
    return test(vulnSeverityTier(worstVulnerability(allVulnerabilitiesForAsset(row))));
  if (field === "cve_id")
    return allVulnerabilitiesForAsset(row).some((v) => test(v.cve_id));
  return test(row[field]);
};

export const applyFilters = (rows, filterModel) => {
  if (!filterModel || filterModel.length === 0) return rows;
  return rows.filter((row) =>
    filterModel.reduce((acc, f, idx) => {
      const hit = matchFilter(row, f);
      if (idx === 0) return hit;
      return f.logic === "or" ? acc || hit : acc && hit;
    }, true)
  );
};
